import { Plus } from "lucide-react";
import { Link } from "@/lib/nav";

import Banner from "@buildpy/ui/banner";
import EmptyState from "@buildpy/ui/empty-state";
import Pager from "@/components/pager";
import { Button } from "@buildpy/ui/button";
import ViewSwitch, { type ViewSwitchItem } from "@/components/view-switch";
import { recentParam } from "@buildpy/core/modules/customer/board";
import CustomerBoard from "@/modules/customer/ui/customer-board";
import CustomerFilters from "@/modules/customer/ui/customer-filters";
import CustomerTable from "@/modules/customer/ui/customer-table";
import { listHref } from "@buildpy/core/modules/list/search";
import type { CustomersView } from "@buildpy/core/views/g1a/customers";
import PageHeader from "@/shell/page-header";

// 보기 전환 — 목록 · 보드(열 = 단계).
const VIEW_ITEMS: ViewSwitchItem[] = [
  { value: null, label: "목록" },
  { value: "board", label: "보드" },
];

// 고객 목록 — 단계 칩 건수는 검색어·담당자까지 건 상태의 단계별 건수이고, 줄은 단계까지 걸어 한 쪽씩(저장소 `search`).
const Page = ({ data }: { data: CustomersView }) => {
  const { filter, view } = data;
  const switcher = (
    <ViewSwitch aria-label="고객 보기 방식" current={view} items={VIEW_ITEMS} keep={{ q: filter.q, assignee: filter.assignee }} path="/app/customers" testId="customer-view-switch" />
  );
  const newButton = (
    <Button asChild size="sm">
      <Link href="/app/customers/new">
        <Plus className="size-4" />
        고객
      </Link>
    </Button>
  );

  if (data.view === "board") {
    const { col, all, members, loaded, colHrefs } = data;

    return (
      <>
        <PageHeader
          actions={
            <>
              {switcher}
              {newButton}
            </>
          }
          meta={loaded ? `${loaded.board.total}명 · 열 = 단계` : undefined}
          title="고객"
        />

        <CustomerFilters all={recentParam(all)} col={col === "INQUIRY" ? null : col} filter={filter} members={members} view="board" />

        {loaded?.phasesFailed && <Banner tone="warning">공정 진행을 불러오지 못했습니다. 시공중 카드의 진행 막대는 잠시 뒤 다시 열어 확인해 주세요.</Banner>}
        {loaded?.lostEventsFailed && <Banner tone="warning">이탈 이력을 불러오지 못해 이탈 처리한 고객은 다시 견적 중이어도 이탈로 두었고, 이탈일이 다를 수 있습니다. 잠시 뒤 다시 열어 확인해 주세요.</Banner>}

        {loaded ? (
          <CustomerBoard board={loaded.board} col={col} colHrefs={colHrefs} />
        ) : (
          <EmptyState>
            <span data-testid="customer-board-failed">고객 보드를 불러오지 못했습니다. 잠시 뒤 다시 열거나 목록 보기로 확인해 주세요.</span>
          </EmptyState>
        )}
      </>
    );
  }

  const { members, total, counts, page, pageCount, rows } = data;

  return (
    <>
      <PageHeader
        actions={
          <>
            {switcher}
            {newButton}
          </>
        }
        meta={`${total}명`}
        title="고객"
      />

      <CustomerFilters
        counts={counts}
        filter={filter}
        members={members}
        total={counts.ALL}
      />

      <CustomerTable
        noCustomers={!filter.q && !filter.stage && !filter.assignee && counts.ALL === 0}
        rows={rows}
      />

      <Pager
        hrefFor={(page) => listHref("/app/customers", { ...filter, page })}
        page={page}
        pageCount={pageCount}
      />
    </>
  );
};

export default Page;
